import { Outlet } from '@tanstack/react-router'
import { useMemo } from 'react'
import { retrieveLaunchParams } from '@telegram-apps/sdk-react'
import { AppRoot } from '@telegram-apps/telegram-ui'
import { TelegramPlatformEnum } from '@/enums/telegram-platform-enum'
import AppInitProvider from '@/providers/app-init-provider'
import { CookiesProvider } from 'react-cookie'
import { isDevEnv } from '@/shared/lib/utils'
import { QueryClientProvider } from '@tanstack/react-query'
import { client } from '@/features/query/client'
import { TonConnectUIProvider } from '@tonconnect/ui-react'
import { Toaster } from '@/ui/sonner'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { OfflineScreen } from '@/features/offline-screen/offline-screen'
import { offlineSignal } from '@/features/offline-screen/signal'
import { useNetworkWatcher } from '@/features/offline-screen/use-network-watcher'
import { SseProvider } from '@/providers/sse-provider'
import { LiveActivitiesProvider } from '@/features/dreams/live-activities'
import { InitWallet } from '@/features/wallet-connect'
import { SessionExpiredDrawer } from '@/features/session-expired/session-expired-drawer'

// URL to the generated TonConnect manifest file.
// In development, serve from local; in production, use the configured base URL.
const TON_CONNECT_MANIFEST_URL = import.meta.env.VITE_MANIFEST_URL

export default function App() {
  const lp = useMemo(() => retrieveLaunchParams(), [])

  const { retry } = useNetworkWatcher()

  return (
    <AppRoot
      appearance={'dark'}
      platform={
        [TelegramPlatformEnum.MacOS, TelegramPlatformEnum.iOS].includes(
          lp.tgWebAppPlatform as TelegramPlatformEnum,
        )
          ? 'ios'
          : 'base'
      }
    >
      <CookiesProvider
        defaultSetOptions={{
          httpOnly: true,
          secure: !isDevEnv(),
          sameSite: 'lax',
          path: '/',
        }}
      >
        <TonConnectUIProvider manifestUrl={TON_CONNECT_MANIFEST_URL}>
          <AppInitProvider>
            <QueryClientProvider client={client}>
              {offlineSignal.value && <OfflineScreen retry={retry} />}
              <InitWallet />
              <LiveActivitiesProvider>
                <SseProvider>
                  <Outlet />
                </SseProvider>
              </LiveActivitiesProvider>
              {import.meta.env.VITE_REACT_QUERY_DEV_TOOLS === 'true' && (
                <ReactQueryDevtools initialIsOpen={false} />
              )}
            </QueryClientProvider>
          </AppInitProvider>
        </TonConnectUIProvider>
      </CookiesProvider>

      <Toaster position="top-center" />
      <SessionExpiredDrawer />
    </AppRoot>
  )
}
